import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { classifyForbidden } from './forbidden-policy';

const REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // Rutas que no llevan el access token y cuyos 401 NO deben disparar un
  // intento de refresco: o no hay sesion todavia, o la sesion es justo lo que
  // esta fallando.
  //
  // Se comparan contra el path SIN prefijo de version (ver extractPathname).
  private readonly publicPaths = new Set([
    '/auth/login',
    '/auth/refresh',
    '/auth/logout',
    '/auth/select-profile',
    '/health',
    '/health/ready',
  ]);
  private refreshInFlight$: Observable<string> | null = null;
  private redirectingToLogin = false;

  constructor(private auth: AuthService, private router: Router) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isPublic = this.isPublicRequest(req.url);
    const token = this.auth.getToken();
    const authReq = !isPublic && token ? this.withAuthHeader(req, token) : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 403) {
          const forbidden = this.classifyForbiddenRequest(error, req.url);
          if (forbidden.kind === 'session-invalid') {
            const reasonParts = ['forbidden-session-invalid', forbidden.source];
            if (forbidden.errorCode) {
              reasonParts.push(forbidden.errorCode);
            }
            this.forceLogout(reasonParts.join(':'));
          }
          return throwError(() => error);
        }

        if (error.status !== 401) {
          return throwError(() => error);
        }

        if (this.isPublicRequest(req.url) || req.url.includes('/auth/refresh')) {
          // Un 401 aqui no significa "sesion caducada": en select-profile es el
          // selectorToken el que ha expirado, y conviene decirlo asi (#448).
          const isProfileSelect =
            this.extractPathname(req.url) === '/auth/select-profile';
          this.forceLogout(
            isProfileSelect ? 'profile-select-expired' : 'unauthorized-public-request',
          );
          return throwError(() => error);
        }

        if (authReq.context.get(REFRESH_RETRIED)) {
          this.forceLogout('refresh-retried-unauthorized');
          return throwError(() => error);
        }

        return this.refreshAccessToken().pipe(
          switchMap((newToken) =>
            next.handle(
              this.withAuthHeader(this.markRetried(authReq), newToken)
            )
          ),
          catchError((retryError: HttpErrorResponse) => {
            if (retryError.status === 401) {
              this.forceLogout('refresh-retry-unauthorized');
            }
            return throwError(() => retryError);
          })
        );
      })
    );
  }

  private withAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private isPublicRequest(url: string): boolean {
    return this.publicPaths.has(this.extractPathname(url));
  }

  private extractPathname(url: string): string {
    try {
      return this.stripApiVersionPrefix(new URL(url, 'http://localhost').pathname);
    } catch {
      return this.stripApiVersionPrefix(url);
    }
  }

  /**
   * Quita el prefijo de version del path (`/v1/auth/login` -> `/auth/login`).
   *
   * `apiBaseUrl` incluye `/v1` en los cuatro entornos, asi que sin esto
   * `publicPaths` no acertaba NUNCA y `isPublicRequest()` devolvia siempre
   * false: un 401 en cualquier endpoint de auth acababa en la rama de refresco
   * (ver issue #448). Se normaliza con un patron en lugar de codificar `/v1`
   * para que un futuro `/v2` no lo rompa otra vez.
   */
  private stripApiVersionPrefix(pathname: string): string {
    return pathname.replace(/^\/v\d+(?=\/)/, '');
  }

  private classifyForbiddenRequest(error: HttpErrorResponse, requestUrl: string) {
    if (requestUrl.includes('/auth/login')) {
      return {
        kind: 'permission' as const,
        source: 'unknown' as const,
        errorCode: null,
        reason: null,
      };
    }
    return classifyForbidden(error, requestUrl);
  }

  private markRetried(req: HttpRequest<unknown>): HttpRequest<unknown> {
    return req.clone({
      context: req.context.set(REFRESH_RETRIED, true),
    });
  }

  private refreshAccessToken(): Observable<string> {
    const refreshToken = this.auth.getRefreshToken();
    if (!refreshToken) {
      this.forceLogout('missing-refresh-token');
      return throwError(() => new Error('Missing refresh token'));
    }

    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.auth.refreshSession().pipe(
        map((response) => response.accessToken),
        catchError((error) => {
          this.forceLogout('refresh-session-failed');
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.refreshInFlight$;
  }

  private forceLogout(reason: string): void {
    if (this.redirectingToLogin) {
      return;
    }
    this.redirectingToLogin = true;
    this.auth.clearSession();
    // Flag the involuntary logout so /login can explain it instead of leaving
    // the user on a bare form (or leaking a raw "Missing refresh token").
    // Expiring the profile selection gets its own notice: there was no session
    // to end, so "tu sesion ha finalizado" would be misleading (#448).
    this.router
      .navigate(['/login'], {
        queryParams: {
          notice: reason === 'profile-select-expired' ? 'profile-select-expired' : 'session-ended',
        },
      })
      .finally(() => {
        this.redirectingToLogin = false;
      });
  }
}
